// Nombres internos de los roles (claim "role" del JWT) y su etiqueta en la interfaz.
export const ROLES = {
  Administrator: 'Administrator',
  MedicalLeaderShip: 'MedicalLeaderShip',
  AdministrativeManagement: 'AdministrativeManagement',
  Doctor: 'Doctor',
  PharmacyManager: 'PharmacyManager',
  WarehouseManager: 'WarehouseManager',
  OperatingRoom: 'OperatingRoom',
  HeadNurse: 'HeadNurse',
  Kitchen: 'Kitchen',
  Maintenance: 'Maintenance',
} as const;

export type RoleName = typeof ROLES[keyof typeof ROLES];

export const ROLE_LABELS: Record<string, string> = {
  Administrator: 'Administrador',
  MedicalLeaderShip: 'Dirección médica',
  AdministrativeManagement: 'Dirección administrativa',
  Doctor: 'Doctor',
  InternNurse: 'Enfermería interna',
  ExternalNurse: 'Enfermería externa',
  PharmacyManager: 'Farmacia',
  WarehouseManager: 'Almacén',
  OperatingRoom: 'Quirófano',
  HeadNurse: 'Jefatura de enfermería',
  Kitchen: 'Cocina',
  Maintenance: 'Mantenimiento',
};

/** Roles que se pueden asignar desde el alta de usuarios (deben coincidir con AllowedValues del API). */
export const ASSIGNABLE_USER_ROLES: RoleName[] = [
  ROLES.Administrator,
  ROLES.MedicalLeaderShip,
  ROLES.AdministrativeManagement,
  ROLES.PharmacyManager,
  ROLES.WarehouseManager,
  ROLES.OperatingRoom,
  ROLES.HeadNurse,
  ROLES.Kitchen,
  ROLES.Maintenance,
];

export function roleLabel(role: string | null | undefined): string {
  return role ? (ROLE_LABELS[role] ?? role) : '';
}

export function currentRole(): string | null {
  return localStorage.getItem('role');
}

// ----------------------------------------------------------------------------
// Rol activo. Un usuario puede tener varios roles (p. ej. Farmacia y Almacén):
// la interfaz trabaja con uno a la vez (menú, guards) y se cambia desde el header
// sin cerrar sesión. Quién hizo qué lo registra el API con el usuario del token,
// y el área queda en el propio documento (bodega).
// ----------------------------------------------------------------------------

const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
const ROLES_KEY = 'roles';
/** Último rol elegido; sobrevive al logout para volver a entrar en la misma área. */
const LAST_ROLE_KEY = 'lastRole';

/** Si no hay un rol recordado, se entra con el primero de esta lista que tenga el usuario. */
const ROLE_PRIORITY: string[] = [
  ROLES.Administrator,
  ROLES.PharmacyManager,
  ROLES.WarehouseManager,
  ROLES.Doctor,
];

const ROLE_HOME: Record<string, string> = {
  [ROLES.Administrator]: '/administracion/consultas-externas',
  [ROLES.Doctor]: '/consulta/pacientes-en-consultorio',
  [ROLES.PharmacyManager]: '/farmacia/ventas',
  [ROLES.WarehouseManager]: '/inventario/productos',
};

/** El claim de rol viene como texto con un rol, o como arreglo con varios. */
export function rolesFromToken(decodedToken: any): string[] {
  const claim = decodedToken?.[ROLE_CLAIM];
  if (!claim) return [];
  return Array.isArray(claim) ? claim : [claim];
}

/** Rol con el que se entra: el último que usó (si aún lo tiene) o el de mayor prioridad. */
export function pickActiveRole(roles: string[]): string | null {
  const last = localStorage.getItem(LAST_ROLE_KEY);
  if (last && roles.includes(last)) return last;
  return ROLE_PRIORITY.find(r => roles.includes(r)) ?? roles[0] ?? null;
}

export function saveSessionRoles(roles: string[], activeRole: string | null): void {
  localStorage.setItem(ROLES_KEY, JSON.stringify(roles));
  if (activeRole) setActiveRole(activeRole);
}

/** Roles del usuario en sesión (sesiones viejas sólo guardaban el rol activo). */
export function availableRoles(): string[] {
  try {
    const roles = JSON.parse(localStorage.getItem(ROLES_KEY) || '[]');
    if (Array.isArray(roles) && roles.length) return roles;
  } catch { /* sesión vieja o valor corrupto */ }
  const role = currentRole();
  return role ? [role] : [];
}

export function setActiveRole(role: string): void {
  localStorage.setItem('role', role);
  localStorage.setItem(LAST_ROLE_KEY, role);
}

export function clearSessionRoles(): void {
  localStorage.removeItem(ROLES_KEY);
}

export function roleHome(role: string | null | undefined): string | null {
  return role ? ROLE_HOME[role] ?? null : null;
}
